"use client";

import * as React from "react";
import { useSession } from "next-auth/react";
import { ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/**
 * Gate de contenido para superadmins. Renderiza children solo si el usuario
 * autenticado tiene `role === "superadmin"`. Mientras la sesión carga, no
 * renderiza nada (evita flash). Si no hay sesión o el rol no califica, no
 * muestra absolutamente nada — el contenido es invisible para el público.
 *
 * Wrap en MDX para documentar features internas:
 *
 *   <AdminOnly>
 *     ## Algo solo para superadmins
 *     ...
 *   </AdminOnly>
 */
export function AdminOnly({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  if (status === "loading") return null;
  const role =
    (session?.user as { role?: string } | undefined)?.role ??
    (session as { user?: { role?: string } } | null)?.user?.role;
  if (role !== "superadmin") return null;

  return (
    <div className="not-prose my-6">
      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardContent className="space-y-3 px-5 py-4">
          <Badge
            variant="outline"
            className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
          >
            <ShieldCheck className="h-3 w-3" />
            Solo superadministradores
          </Badge>
          <div className="prose prose-sm max-w-none dark:prose-invert">
            {children}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
